import { useState, useEffect } from 'react'
import { WifiOff, RefreshCw } from 'lucide-react'
import { useOnlineStatus } from '@/hooks'
import { offlineQueue } from '@/lib/offlineQueue'

export function OfflineIndicator() {
  const isOnline = useOnlineStatus()
  const [pendingCount, setPendingCount] = useState(0)
  const [isSyncing, setIsSyncing] = useState(false)

  // Poll pending count periodically
  useEffect(() => {
    const updateCount = async () => {
      const count = await offlineQueue.getPendingCount()
      setPendingCount(count)
    }

    updateCount()
    const interval = setInterval(updateCount, 5000)
    return () => clearInterval(interval)
  }, [])

  // Auto-sync when online and there are pending requests
  useEffect(() => {
    const syncPending = async () => {
      if (isOnline && pendingCount > 0 && !isSyncing) {
        setIsSyncing(true)
        try {
          await offlineQueue.processQueue()
          const newCount = await offlineQueue.getPendingCount()
          setPendingCount(newCount)
        } finally {
          setIsSyncing(false)
        }
      }
    }

    syncPending()
  }, [isOnline, pendingCount, isSyncing])

  // Don't render if online and no pending requests
  if (isOnline && pendingCount === 0) {
    return null
  }

  return (
    <div
      className={`fixed top-0 inset-x-0 py-2 px-4 text-center text-sm z-50 flex items-center justify-center gap-2
        ${isOnline ? 'bg-yellow-600 text-yellow-50' : 'bg-red-600 text-red-50'}`}
      role="status"
      aria-live="polite"
    >
      {isOnline ? (
        <>
          <RefreshCw
            className={`h-4 w-4 ${isSyncing ? 'animate-spin' : ''}`}
            aria-hidden="true"
          />
          <span>
            Syncing {pendingCount} pending{' '}
            {pendingCount === 1 ? 'change' : 'changes'}...
          </span>
        </>
      ) : (
        <>
          <WifiOff className="h-4 w-4" aria-hidden="true" />
          <span>You are offline. Changes will sync when connected.</span>
          {pendingCount > 0 && (
            <span className="ml-1">
              ({pendingCount} pending)
            </span>
          )}
        </>
      )}
    </div>
  )
}
